import { useId, useState, type FormEvent } from 'react';
import { cancelPortalAppointment } from '../../core/api/portal-service';
import { describeApiError } from '../../core/errors/describe-api-error';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import { Alert } from '../../ui/alert';
import { Button } from '../../ui/button';

/**
 * Calling off a consultation, behind a second press and a reason.
 *
 * The reason is required because the server requires it: a closure with no reason is invisible to
 * the reporting that reads it. It is asked for here rather than turned away by a round trip.
 */
export function AppointmentCancelForm({
    appointmentId,
    onCancelled
}: {
    appointmentId: string;
    onCancelled: (appointmentId: string) => void;
}) {
    const { t } = useTranslation();
    const reasonId = useId();

    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<TranslationKey | null>(null);

    if (!open) {
        return (
            <Button type="button" variant="secondary" size="sm" className="mt-3" onClick={() => setOpen(true)}>
                {t('portal.cancel_appointment')}
            </Button>
        );
    }

    async function submit(event: FormEvent): Promise<void> {
        event.preventDefault();

        if (sending) return;

        if (reason.trim() === '') {
            setError('portal.cancel_appointment_reason_required');
            return;
        }

        setSending(true);
        setError(null);

        try {
            await cancelPortalAppointment(appointmentId, reason.trim());
            onCancelled(appointmentId);
        } catch (cause) {
            setError(describeApiError(cause));
            setSending(false);
        }
    }

    return (
        <form onSubmit={(event) => void submit(event)} className="mt-3 space-y-3">
            <label htmlFor={reasonId} className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('portal.cancel_appointment_reason')}
            </label>
            <textarea
                id={reasonId}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={2}
                maxLength={512}
                placeholder={t('portal.cancel_appointment_reason_placeholder')}
                className="w-full rounded-xl border border-slate-300 bg-white p-3 text-base text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 sm:text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-brand-500 dark:focus:ring-brand-950"
            />

            {error !== null && <Alert>{t(error)}</Alert>}

            <div className="flex flex-wrap gap-2">
                <Button type="submit" variant="danger" size="sm" disabled={sending}>
                    {sending ? t('portal.cancel_appointment_sending') : t('portal.cancel_appointment_confirm')}
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={sending}
                    onClick={() => {
                        setOpen(false);
                        setReason('');
                        setError(null);
                    }}
                >
                    {t('portal.cancel_appointment_keep')}
                </Button>
            </div>
        </form>
    );
}
